import { z } from 'zod';
import { MAX_CHAT_LENGTH, MAX_DISPLAY_NAME_LENGTH, MIN_DISPLAY_NAME_LENGTH } from './constants.js';
import { DRINK_KINDS, MAX_MOTTO_LENGTH } from './catalogue.js';
import { economyClientMessageSchema, economyServerMessageSchema } from './economy.js';
import { gameClientMessageSchema, gameServerMessageSchema } from './games.js';
import { socialClientMessageSchema, socialServerMessageSchema } from './social.js';
import { arcadeClientMessageSchema, arcadeServerMessageSchema } from './arcade.js';
import { petClientMessageSchema, petServerMessageSchema } from './pets.js';
import { gardenClientMessageSchema, gardenServerMessageSchema } from './garden.js';
import { tradeServerMessageSchema } from './trade.js';
import { relationshipServerMessageSchema } from './relationships.js';
import { voiceClientMessageSchema, voiceServerMessageSchema } from './voice.js';
import { announceClientMessageSchema, announceServerMessageSchema } from './announce.js';
import { rewardsClientMessageSchema, rewardsServerMessageSchema } from './rewards.js';
import { jukeboxClientMessageSchema, jukeboxServerMessageSchema } from './jukebox.js';
import { clubhouseServerMessageSchema } from './clubhouse.js';
import type { PublicClubhouse } from './clubhouse.js';
import { guideClientMessageSchema } from './guides.js';
import type { PublicRelationship } from './relationships.js';
import { lookSchema } from './look.js';
import type { Look } from './look.js';
import type { UserRole } from './roles.js';

export { lookSchema };

/* -------------------------------------------------------------------------- */
/* Shared value objects                                                       */
/* -------------------------------------------------------------------------- */

export const tileSchema = z.object({
  x: z.number().int().min(0).max(255),
  y: z.number().int().min(0).max(255),
});

export const directionSchema = z.number().int().min(0).max(7);

export const drinkKindSchema = z.enum(DRINK_KINDS);

export const avatarStateSchema = z.object({
  id: z.string(),
  name: z.string(),
  x: z.number(),
  y: z.number(),
  z: z.number(),
  direction: directionSchema,
  look: lookSchema,
  /** Remaining tiles the server intends to walk this avatar through. */
  path: z.array(tileSchema),
  /** Server clock time (ms) at which the current step began. */
  stepStartedAt: z.number(),
  dancing: z.boolean(),
  /** Set by `:afk`; cleared the moment they walk anywhere. */
  afk: z.boolean(),
  /** Drink in hand, if any. */
  carrying: drinkKindSchema.nullable(),
  /** True for a guide (Bot Kaki): a scripted NPC, not a player. */
  npc: z.boolean().optional(),
});

/**
 * What a room is for. Public places are always listed in the navigator;
 * player rooms only appear while someone is inside.
 */
export const ROOM_KINDS = ['public', 'home', 'shop', 'mall', 'office', 'lounge'] as const;
export const roomKindSchema = z.enum(ROOM_KINDS);
export type RoomKind = z.infer<typeof roomKindSchema>;

export const roomDescriptorSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Newline-joined heightmap rows; parse with `parseHeightmap`. */
  heightmap: z.string(),
  door: tileSchema,
  doorDirection: directionSchema,
  /** Null for public rooms, which nobody may redecorate. */
  ownerId: z.string().nullable(),
  ownerName: z.string().nullable(),
  kind: roomKindSchema,
  /** Seed key of a public room that has a Bot Kaki guide (see guides.ts). */
  guideKey: z.string().nullable().optional(),
  /** Stable seed key of a public room (e.g. 'dog_run'), for room-specific features. Null for player rooms. */
  seedKey: z.string().nullable(),
  description: z.string(),
  /** Wallpaper and floor style ids (see roomStyle.ts); unknown ids fall back to the default. */
  wallStyle: z.string().max(48),
  floorStyle: z.string().max(48),
  /** Ids of players the owner trusts to move furniture and operate doors. Always empty for public rooms. */
  rights: z.array(z.string()),
});

/** A piece of furniture placed in a room. Rotation is a facing (0, 2, 4, 6). */
export const furniItemSchema = z.object({
  id: z.string(),
  kind: z.string(),
  x: z.number().int(),
  y: z.number().int(),
  z: z.number(),
  rotation: directionSchema,
  /** Doors only: open lets people walk through; closed blocks like a wall. Always false otherwise. */
  open: z.boolean(),
});


export const chatMessageSchema = z.object({
  id: z.string(),
  avatarId: z.string(),
  name: z.string(),
  body: z.string(),
  at: z.number(),
});

/* -------------------------------------------------------------------------- */
/* Client -> server                                                           */
/* -------------------------------------------------------------------------- */

export const coreClientMessageSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('ping'), seq: z.number().int().nonnegative() }),
  z.object({ t: z.literal('room:join'), roomId: z.string().min(1).max(64) }),
  z.object({ t: z.literal('room:leave') }),
  /** Request to walk to a tile. The server pathfinds and decides. */
  z.object({ t: z.literal('move'), x: z.number().int().min(0).max(255), y: z.number().int().min(0).max(255) }),
  z.object({ t: z.literal('turn'), direction: directionSchema }),
  z.object({ t: z.literal('chat'), body: z.string().min(1).max(MAX_CHAT_LENGTH) }),
  /** One-shot wave, start/stop dancing, or set/clear `:afk`. */
  z.object({ t: z.literal('action'), action: z.enum(['wave', 'dance', 'stop', 'afk', 'unafk']) }),
  z.object({ t: z.literal('typing'), typing: z.boolean() }),
  /** Order a drink. Only works standing next to a service counter. */
  z.object({ t: z.literal('drink:order'), drink: drinkKindSchema }),
  z.object({ t: z.literal('drink:finish') }),
  /** Give another player one of your daily respects. */
  z.object({ t: z.literal('respect'), avatarId: z.string().min(1).max(64) }),
  /** Room owner only: move an inventory item into the room. */
  z.object({
    t: z.literal('furni:place'),
    inventoryId: z.string().min(1).max(64),
    x: z.number().int().min(0).max(255),
    y: z.number().int().min(0).max(255),
    rotation: directionSchema,
  }),
  /** Room owner only: return an item to the inventory of whoever placed it. */
  z.object({ t: z.literal('furni:pickup'), itemId: z.string().min(1).max(64) }),
  /** Room owner only: move a placed item to another tile without picking it up. */
  z.object({
    t: z.literal('furni:move'),
    itemId: z.string().min(1).max(64),
    x: z.number().int().min(0).max(255),
    y: z.number().int().min(0).max(255),
    rotation: directionSchema,
  }),
  /** Room owner only: turn an item a quarter. */
  z.object({ t: z.literal('furni:rotate'), itemId: z.string().min(1).max(64) }),
  /** Owner or a player with rights: open or close a door item. */
  z.object({ t: z.literal('furni:toggleDoor'), itemId: z.string().min(1).max(64) }),
  /** Room owner only: send a player back to the home view. */
  z.object({ t: z.literal('room:kick'), avatarId: z.string().min(1).max(64) }),
  /** Room owner only: let another player move furniture and operate doors here. */
  z.object({ t: z.literal('room:grantRights'), avatarId: z.string().min(1).max(64) }),
  /** Room owner only: withdraw rights previously given. */
  z.object({ t: z.literal('room:revokeRights'), avatarId: z.string().min(1).max(64) }),
]);

/** Every client message: core plus each feature module's own union. */
export const clientMessageSchema = z.union([
  coreClientMessageSchema,
  gameClientMessageSchema,
  economyClientMessageSchema,
  socialClientMessageSchema,
  arcadeClientMessageSchema,
  petClientMessageSchema,
  gardenClientMessageSchema,
  voiceClientMessageSchema,
  jukeboxClientMessageSchema,
  announceClientMessageSchema,
  rewardsClientMessageSchema,
  guideClientMessageSchema,
]);

export type ClientMessage = z.infer<typeof clientMessageSchema>;
export type CoreClientMessage = z.infer<typeof coreClientMessageSchema>;

/* -------------------------------------------------------------------------- */
/* Server -> client                                                           */
/* -------------------------------------------------------------------------- */

export const ERROR_CODES = [
  'unauthorized',
  'bad_request',
  'not_found',
  'rate_limited',
  'not_in_room',
  'already_in_room',
  'room_full',
  'unreachable',
  'forbidden',
  'occupied',
  'planted',
  'duplicate',
  'too_far',
  'no_respects',
  'guest_restricted',
  'muted',
  'banned',
  'insufficient_funds',
  'internal',
] as const;

export const errorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

export const coreServerMessageSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('pong'), seq: z.number(), serverTime: z.number() }),
  z.object({
    t: z.literal('welcome'),
    selfId: z.string(),
    name: z.string(),
    look: lookSchema,
    serverTime: z.number(),
  }),
  z.object({
    t: z.literal('room:joined'),
    room: roomDescriptorSchema,
    occupants: z.array(avatarStateSchema),
    items: z.array(furniItemSchema),
    serverTime: z.number(),
  }),
  z.object({ t: z.literal('room:left'), roomId: z.string() }),
  /** The owner changed the wallpaper or floor of the room you are in. */
  z.object({ t: z.literal('room:style'), wallStyle: z.string(), floorStyle: z.string() }),
  /** The room's rights list changed (a grant or a revoke). */
  z.object({ t: z.literal('room:rights'), rights: z.array(z.string()) }),
  z.object({ t: z.literal('avatar:enter'), avatar: avatarStateSchema }),
  z.object({ t: z.literal('avatar:leave'), avatarId: z.string() }),
  /**
   * The server has committed an avatar to a route. Clients interpolate along
   * `path` from `from`, one tile per STEP_DURATION_MS, starting at `startedAt`.
   */
  z.object({
    t: z.literal('avatar:path'),
    avatarId: z.string(),
    from: z.object({ x: z.number(), y: z.number(), z: z.number() }),
    path: z.array(tileSchema),
    startedAt: z.number(),
  }),
  z.object({ t: z.literal('avatar:turn'), avatarId: z.string(), direction: directionSchema }),
  z.object({ t: z.literal('avatar:wave'), avatarId: z.string() }),
  /** Persistent presentation state changed (dance, drink). */
  z.object({
    t: z.literal('avatar:update'),
    avatarId: z.string(),
    dancing: z.boolean(),
    afk: z.boolean(),
    carrying: drinkKindSchema.nullable(),
  }),
  /** Appearance or name changed while in the room. */
  z.object({ t: z.literal('avatar:look'), avatarId: z.string(), name: z.string(), look: lookSchema }),
  z.object({ t: z.literal('avatar:typing'), avatarId: z.string(), typing: z.boolean() }),
  z.object({
    t: z.literal('avatar:respect'),
    fromId: z.string(),
    fromName: z.string(),
    toId: z.string(),
    toName: z.string(),
  }),
  z.object({ t: z.literal('furni:add'), item: furniItemSchema }),
  z.object({ t: z.literal('furni:update'), item: furniItemSchema }),
  z.object({ t: z.literal('furni:remove'), itemId: z.string() }),
  /** Sent only to the affected player: their balance in Kaki Dollars. */
  z.object({
    t: z.literal('wallet'),
    money: z.number().int(),
    respectsLeft: z.number().int(),
    gold: z.number().int().optional(),
    diamond: z.number().int().optional(),
  }),
  /** Sent only to the affected player: their inventory changed, refetch it. */
  z.object({ t: z.literal('inventory:changed') }),
  /** Sent only to the affected player: a friendly toast. */
  z.object({
    t: z.literal('notice'),
    tone: z.enum(['info', 'success', 'warning']),
    message: z.string(),
  }),
  /** Periodic authoritative correction for interpolation drift. */
  z.object({
    t: z.literal('room:snapshot'),
    serverTime: z.number(),
    avatars: z.array(avatarStateSchema),
  }),
  z.object({ t: z.literal('chat'), message: chatMessageSchema }),
  z.object({ t: z.literal('error'), code: errorCodeSchema, message: z.string() }),
]);

/** Every server message: core plus each feature module's own union. */
export const serverMessageSchema = z.union([
  coreServerMessageSchema,
  gameServerMessageSchema,
  economyServerMessageSchema,
  socialServerMessageSchema,
  arcadeServerMessageSchema,
  petServerMessageSchema,
  gardenServerMessageSchema,
  tradeServerMessageSchema,
  relationshipServerMessageSchema,
  voiceServerMessageSchema,
  jukeboxServerMessageSchema,
  announceServerMessageSchema,
  rewardsServerMessageSchema,
  clubhouseServerMessageSchema,
]);

export type ServerMessage = z.infer<typeof serverMessageSchema>;
export type CoreServerMessage = z.infer<typeof coreServerMessageSchema>;

export type Tile = z.infer<typeof tileSchema>;
export type { Look };
export type AvatarState = z.infer<typeof avatarStateSchema>;
export type RoomDescriptor = z.infer<typeof roomDescriptorSchema>;
export type ChatMessage = z.infer<typeof chatMessageSchema>;
export type FurniItem = z.infer<typeof furniItemSchema>;

/* -------------------------------------------------------------------------- */
/* HTTP API shapes                                                            */
/* -------------------------------------------------------------------------- */

/** The signed-in player, as returned by /api/me and the auth endpoints. */
export interface SelfProfile {
  id: string;
  username: string;
  displayName: string;
  isGuest: boolean;
  look: Look;
  motto: string;
  /** Balance in Kaki Dollars. */
  money: number;
  /** Premium balances; never earned in play (see premium.ts). */
  gold: number;
  diamond: number;
  respectsReceived: number;
  respectsLeft: number;
  role: UserRole;
  /** When the player accepted the terms and confirmed they are 13 or older; null if never. */
  acceptedTermsAt: number | null;
  /** True once the player has dismissed the welcome popup. */
  welcomeSeen: boolean;
  /** Badge ids chosen to feature on the profile card, in order (see MAX_DISPLAY_BADGES). */
  displayBadges: string[];
}

/** Another player's public card. */
export interface PublicProfile {
  id: string;
  displayName: string;
  isGuest: boolean;
  look: Look;
  motto: string;
  respectsReceived: number;
  createdAt: number;
  /** Everything except 'player' gets a badge on the info card. */
  role: UserRole;
  /** Accepted relationships (relationships feature). */
  relationships?: PublicRelationship[];
  /** Earned achievements (social feature). */
  badges?: Array<{ id: string; earnedAt: number }>;
  /** Number of accepted friends (social feature). */
  friendCount?: number;
  /** The clubhouse they lead or belong to, if any. */
  clubhouse?: PublicClubhouse;
}

export interface RoomSummary {
  id: string;
  name: string;
  kind: RoomKind;
  description: string;
  ownerId: string | null;
  ownerName: string | null;
  maxOccupants: number;
  occupants: number;
  /** Players who liked the room (social feature). */
  likes?: number;
}

export interface InventoryItem {
  id: string;
  kind: string;
  createdAt: number;
}

export interface LeaderboardEntry {
  id: string;
  displayName: string;
  look: Look;
  value: number;
}

/* -------------------------------------------------------------------------- */
/* Wire helpers                                                               */
/* -------------------------------------------------------------------------- */

export function encodeMessage(message: ClientMessage | ServerMessage): string {
  return JSON.stringify(message);
}

export type DecodeResult<T> = { ok: true; value: T } | { ok: false; error: string };

function decodeWith<T>(schema: z.ZodType<T>, raw: string): DecodeResult<T> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, error: 'Payload is not valid JSON' };
  }
  const result = schema.safeParse(parsed);
  if (!result.success) {
    return { ok: false, error: result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') };
  }
  return { ok: true, value: result.data };
}

export function decodeClientMessage(raw: string): DecodeResult<ClientMessage> {
  return decodeWith(clientMessageSchema, raw);
}

export function decodeServerMessage(raw: string): DecodeResult<ServerMessage> {
  return decodeWith(serverMessageSchema, raw);
}

/* -------------------------------------------------------------------------- */
/* Validation shared with the HTTP layer                                      */
/* -------------------------------------------------------------------------- */

export const displayNameSchema = z
  .string()
  .trim()
  .min(MIN_DISPLAY_NAME_LENGTH)
  .max(MAX_DISPLAY_NAME_LENGTH)
  .regex(/^[\p{L}\p{N}][\p{L}\p{N} _-]*$/u, 'Use letters, numbers, spaces, hyphens or underscores');

export const passwordSchema = z.string().min(8).max(128);

export const mottoSchema = z.string().trim().max(MAX_MOTTO_LENGTH);
